import { useState } from "react";
import { Trash2, Star, StarOff, Tag, Folder, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

interface BulkActionsBarProps {
  selectedCount: number;
  selectedIds: string[];
  onClearSelection: () => void;
  onBulkDelete: (ids: string[]) => void;
  onBulkToggleFavorite: (ids: string[], makeFavorite: boolean) => void;
  onBulkAddTags: (ids: string[], tags: string[]) => void;
  onBulkRemoveTags: (ids: string[], tags: string[]) => void;
  onBulkSetCategory: (ids: string[], category: string | undefined) => void;
  availableCategories: string[];
  availableTags: string[];
}

export function BulkActionsBar({
  selectedCount,
  selectedIds,
  onClearSelection,
  onBulkDelete,
  onBulkToggleFavorite,
  onBulkAddTags,
  onBulkRemoveTags,
  onBulkSetCategory,
  availableCategories,
  availableTags,
}: BulkActionsBarProps) {
  const { toast } = useToast();
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");

  const handleBulkDelete = () => {
    if (selectedCount === 0) return;
    
    onBulkDelete(selectedIds);
    toast({
      title: "Entries deleted",
      description: `${selectedCount} translation${selectedCount > 1 ? 's' : ''} deleted`,
    });
  };

  const handleBulkFavorite = (makeFavorite: boolean) => {
    if (selectedCount === 0) return;
    
    onBulkToggleFavorite(selectedIds, makeFavorite);
    toast({
      title: makeFavorite ? "Added to favorites" : "Removed from favorites",
      description: `${selectedCount} translation${selectedCount > 1 ? 's' : ''} updated`,
    });
  };

  const handleAddTags = () => {
    const tagsToAdd = newTag.trim() 
      ? [newTag.trim()] 
      : [];
    
    if (tagsToAdd.length === 0) {
      toast({
        title: "No tag specified",
        description: "Please enter a tag name",
        variant: "destructive",
      });
      return;
    }

    onBulkAddTags(selectedIds, tagsToAdd);
    setNewTag("");
    setTagsDialogOpen(false);
    toast({
      title: "Tags added",
      description: `Added "${tagsToAdd[0]}" to ${selectedCount} translation${selectedCount > 1 ? 's' : ''}`,
    });
  };

  const handleRemoveExistingTag = (tag: string) => {
    onBulkRemoveTags(selectedIds, [tag]);
    toast({
      title: "Tag removed",
      description: `Removed "${tag}" from ${selectedCount} translation${selectedCount > 1 ? 's' : ''}`,
    });
  };

  const handleSetCategory = () => {
    const categoryToSet = newCategory.trim() || selectedCategory;
    
    if (!categoryToSet) {
      toast({
        title: "No category specified",
        description: "Please select or enter a category",
        variant: "destructive",
      });
      return;
    }

    onBulkSetCategory(selectedIds, categoryToSet);
    setNewCategory("");
    setSelectedCategory("");
    setCategoryDialogOpen(false);
    toast({
      title: "Category updated",
      description: `Set category to "${categoryToSet}" for ${selectedCount} translation${selectedCount > 1 ? 's' : ''}`,
    });
  };

  const handleRemoveCategory = () => {
    onBulkSetCategory(selectedIds, undefined);
    setCategoryDialogOpen(false);
    toast({
      title: "Category removed",
      description: `Removed category from ${selectedCount} translation${selectedCount > 1 ? 's' : ''}`,
    });
  };

  if (selectedCount === 0) return null;

  return (
    <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Badge variant="default" className="bg-primary">
            {selectedCount} selected
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearSelection}
            className="text-xs"
          >
            <X className="h-3 w-3 mr-1" />
            Clear
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Favorite Actions */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleBulkFavorite(true)}
            className="text-xs"
          >
            <Star className="h-3 w-3 mr-1" />
            Add to Favorites
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => handleBulkFavorite(false)}
            className="text-xs"
          >
            <StarOff className="h-3 w-3 mr-1" />
            Remove from Favorites
          </Button>

          {/* Tags Dialog */}
          <Dialog open={tagsDialogOpen} onOpenChange={setTagsDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-xs">
                <Tag className="h-3 w-3 mr-1" />
                Manage Tags
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Manage Tags for {selectedCount} Item{selectedCount > 1 ? 's' : ''}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Add New Tag</label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter tag name"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddTags()}
                    />
                    <Button onClick={handleAddTags} size="sm">
                      Add
                    </Button>
                  </div>
                </div>

                {availableTags.length > 0 && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">Remove Existing Tags</label>
                    <div className="flex flex-wrap gap-1">
                      {availableTags.map((tag) => (
                        <Badge
                          key={tag}
                          variant="outline"
                          className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                          onClick={() => handleRemoveExistingTag(tag)}
                        >
                          {tag} ✕
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Category Dialog */}
          <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-xs">
                <Folder className="h-3 w-3 mr-1" />
                Set Category
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Set Category for {selectedCount} Item{selectedCount > 1 ? 's' : ''}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {availableCategories.length > 0 && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">Select Existing Category</label>
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a category" />
                      </SelectTrigger>
                      <SelectContent className="z-50 bg-popover">
                        {availableCategories.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium mb-2 block">Or Create New Category</label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter category name"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSetCategory()}
                    />
                    <Button onClick={handleSetCategory} size="sm">
                      Set
                    </Button>
                  </div>
                </div>

                <div className="flex gap-2 pt-2 border-t">
                  <Button onClick={handleRemoveCategory} variant="outline" size="sm">
                    Remove Category
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Delete Action */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleBulkDelete}
            className="text-xs hover:bg-destructive hover:text-destructive-foreground"
          >
            <Trash2 className="h-3 w-3 mr-1" />
            Delete All
          </Button>
        </div>
      </div>
    </div>
  );
}